import { observer } from 'mobx-react';
import { useMemo } from 'react';
import { useStore } from '../hooks/use-store';
import { ResourcePermission } from '../types/users-types';
import { arrayToMap } from '../utils/object-utils';

export interface HasPermissionsProps {
  permissions: ResourcePermission[];
  children: any;
}

export const checkIfAnyPermissionAllowed = (
  allowedPermissions: ResourcePermission[] | undefined,
  permissionsToCheck: ResourcePermission[] | undefined,
): boolean => {
  if (!allowedPermissions || !permissionsToCheck) return false;
  return allowedPermissions.some(ap =>
    permissionsToCheck
      .filter(rpToCheck => ap.resourceId === rpToCheck.resourceId)
      .some(rpToCheck =>
        ap.permissions
          .map(p => p.id)
          // eslint-disable-next-line sonarjs/no-nested-functions
          .some(p => rpToCheck.permissions.map(per => per.id).includes(p)),
      ),
  );
};

export const checkSinglePermission = (
  allowedPermissions: ResourcePermission[],
  resourceId: string,
  permissionId: string,
): boolean =>
  checkIfAnyPermissionAllowed(allowedPermissions, [
    { resourceId, permissions: [{ id: permissionId }] },
  ]);

export const useCheckPermissions = (permissions: ResourcePermission[]): boolean => {
  const { userPermissions } = useStore().apiStore;
  return useMemo(
    () => checkIfAnyPermissionAllowed(userPermissions, permissions),
    [userPermissions, permissions],
  );
};

export const useCheckSinglePermission = (resourceId: string, permissionId: string): boolean =>
  useCheckPermissions([{ resourceId, permissions: [{ id: permissionId }] }]);

export const useCheckSingleSapPermission = (
  sapIds: string[],
  resourceId: string,
  permissionId: string,
): Record<string, boolean> => {
  const { userSapPermissions, userGlobalPermissions } = useStore().apiStore;
  return useMemo(() => {
    const permissionMap = arrayToMap(userSapPermissions, 'sapId');
    const value: Record<string, boolean> = {};
    const permissionToCheck: ResourcePermission = {
      resourceId,
      permissions: [{ id: permissionId }],
    };
    const globalAllow = checkIfAnyPermissionAllowed(userGlobalPermissions, [permissionToCheck]);
    sapIds.forEach(sapId => {
      value[sapId] =
        globalAllow ||
        checkIfAnyPermissionAllowed(permissionMap[sapId]?.resourcePermissions, [permissionToCheck]);
    });
    return value;
  }, [permissionId, resourceId, sapIds, userGlobalPermissions, userSapPermissions]);
};

const HasPermissions = observer((props: HasPermissionsProps) =>
  useCheckPermissions(props.permissions) ? props.children : null,
);

export default HasPermissions;
